import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ExternalLink, Home, Shirt, User } from 'lucide-react';
import { navGroups, isGroupActive } from '@/components/navigation';

export default function MobileNav({ onNavigate }) {
  const location = useLocation();
  const [expanded, setExpanded] = useState(() => navGroups.find((g) => isGroupActive(g, location.pathname))?.id ?? null);

  const rowCls = (active) =>
    `flex items-center gap-3 w-full py-3 px-4 rounded-xl text-left transition-colors ${active ? 'bg-secondary/15 text-secondary' : 'text-foreground hover:bg-muted'}`;

  return (
    <div className="lg:hidden bg-background/97 backdrop-blur-xl border-t border-white/5 max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="mobile-nav">
      <div className="container-custom py-4 space-y-1">
        <Link to="/" className={rowCls(location.pathname === '/')} onClick={onNavigate} data-testid="mobile-nav-home">
          <Home className="w-5 h-5" /> <span className="font-semibold">Home</span>
        </Link>

        {navGroups.map((group) => {
          const isOpen = expanded === group.id;
          return (
            <div key={group.id} className="rounded-xl border border-border/50 overflow-hidden">
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : group.id)}
                aria-expanded={isOpen}
                className={rowCls(isGroupActive(group, location.pathname))}
                data-testid={`mobile-nav-group-${group.id}`}
              >
                <group.icon className="w-5 h-5" />
                <span className="font-semibold flex-1">{group.label}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOpen && (
                <div className="bg-card/60 px-2 pb-2">
                  {group.items.map((item) => {
                    const cls = 'flex items-center gap-3 py-2.5 px-3 pl-4 rounded-lg text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors';
                    const testId = `mobile-nav-item-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
                    const body = (
                      <>
                        <item.icon className="w-4 h-4 text-secondary" />
                        <span className={`flex-1 ${location.pathname === item.to ? 'text-secondary font-semibold' : ''}`}>{item.label}</span>
                        {item.tag && <span className="px-1.5 py-0.5 bg-secondary text-secondary-foreground text-[10px] font-bold rounded">{item.tag}</span>}
                        {item.href && <ExternalLink className="w-3 h-3" />}
                      </>
                    );
                    return item.href ? (
                      <a key={item.label} href={item.href} target="_blank" rel="noopener noreferrer" className={cls} onClick={onNavigate} data-testid={testId}>{body}</a>
                    ) : (
                      <Link key={item.label} to={item.to} className={cls} onClick={onNavigate} data-testid={testId}>{body}</Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        <Link to="/shop" className={rowCls(location.pathname === '/shop')} onClick={onNavigate} data-testid="mobile-nav-shop">
          <Shirt className="w-5 h-5" /> <span className="font-semibold">Shop</span>
        </Link>
        <Link to="/about" className={rowCls(location.pathname === '/about')} onClick={onNavigate} data-testid="mobile-nav-about">
          <User className="w-5 h-5" /> <span className="font-semibold">About</span>
        </Link>
        <Link
          to="/donate"
          className="block mt-2 py-3 px-4 rounded-xl bg-accent text-accent-foreground font-semibold text-center"
          onClick={onNavigate}
          data-testid="mobile-nav-donate"
        >
          Donate
        </Link>
      </div>
    </div>
  );
}
